<template>
  <n-config-provider :theme-overrides="themeOverrides">
    <n-loading-bar-provider>
      <n-dialog-provider>
        <DialogContent />
        <n-message-provider>
          <MessageContent />
          <router-view />
        </n-message-provider>
      </n-dialog-provider>
    </n-loading-bar-provider>
  </n-config-provider>
</template>
<script lang="ts" setup>
import {
  NConfigProvider,
  NMessageProvider,
  NDialogProvider,
  NLoadingBarProvider,
  GlobalThemeOverrides,
} from 'naive-ui';

import DialogContent from '@/components/DialogContent.vue';
import MessageContent from '@/components/MessageContent.vue';

const primaryColor = '#572DF3';
const colorPressed = '#21009a';

const themeOverrides: GlobalThemeOverrides = {
  common: {
    primaryColor: primaryColor,
    primaryColorHover: primaryColor,
    fontSize: `${16 / 192}rem`,
  },
  Input: {
    borderRadius: '0',
    heightLarge: `${50 / 192}rem`,
    heightMedium: `${40 / 192}rem`,
    heightSmall: `${28 / 192}rem`,
    fontSizeSmall: `${12 / 192}rem`,
    fontSizeMedium: `${15 / 192}rem`,
    textColorDisabled: '#666',
  },
  Button: {
    fontWeightStrong: 'bold',
    fontSizeLarge: `${15 / 192}rem`,
    borderRadiusLarge: '0',
    iconSizeLarge: `${18 / 192}rem`,
    heightLarge: `${50 / 192}rem`,
    paddingLarge: `0 ${30 / 191}rem`,
    fontSizeMedium: `${15 / 192}rem`,
    textColorTextPrimary: primaryColor,
    borderRadiusMedium: '0',
    iconSizeMedium: `${18 / 192}rem`,
    heightMedium: `${40 / 192}rem`,
    heightSmall: `${28 / 192}rem`,
    fontSizeSmall: `${12 / 192}rem`,
    colorPressed: colorPressed,
  },
  DataTable: {
    fontSizeMedium: `${15 / 192}rem`,
  },
  Table: {
    fontSizeMedium: `${15 / 192}rem`,
  },
  Select: {
    peers: {
      InternalSelection: {
        heightLarge: `${50 / 192}rem`,
        heightMedium: `${40 / 192}rem`,
        heightSmall: `${28 / 192}rem`,
        fontSizeLarge: `${15 / 192}rem`,
        fontSizeMedium: `${16 / 192}rem`,
        fontSizeSmall: `${12 / 192}rem`,
        borderRadius: '0',
        textColorDisabled: '#666',
      },
      InternalSelectMenu: {
        optionFontSizeMedium: `${15 / 192}rem`,
        optionFontSizeLarge: `${15 / 192}rem`,
        borderRadius: '6px',
      },
    },
  },
  Tag: {
    fontSizeMedium: `${12 / 192}rem`,
    heightMedium: `${28 / 192}rem`,
    fontSizeSmall: `${12 / 192}rem`,
  },
  TreeSelect: {
    peers: {
      InternalSelection: {
        fontSizeMedium: `${16 / 192}rem`,
        heightMedium: `${40 / 192}rem`,
      },
      Tree: {
        fontSize: `${15 / 192}rem`,
      },
    },
  },
  AutoComplete: {
    peers: {
      Input: {
        fontSizeLarge: `${18 / 192}rem`,
        iconSize: `${20 / 192}rem`,
      },
    },
  },
  Pagination: {
    itemColor: '#fff',
    itemColorActive: primaryColor,
    itemTextColorActive: '#fff',
    itemBorderActive: primaryColor,
    itemColorActiveHover: primaryColor,
    itemFontSizeMedium: `${16 / 192}rem`,
    itemSizeMedium: `${30 / 192}rem`,
    buttonColor: '#fff',
    peers: {
      Select: {
        peers: {
          InternalSelection: {
            heightSmall: `${30 / 192}rem`,
            fontSizeSmall: `${16 / 192}rem`,
          },
        },
      },
    },
  },
  Card: {
    titleFontSizeMedium: `${18 / 192}rem`,
    borderColor: 'transparent',
  },
  Descriptions: {
    fontSizeMedium: `${18 / 192}rem`,
  },
  Tabs: {
    tabFontSizeLarge: `${16 / 192}rem`,
  },
  Popover: {
    fontSize: `${16 / 192}rem`,
  },
  Popselect: {
    peers: {
      Popover: {
        fontSize: `${18 / 192}rem`,
      },
      InternalSelectMenu: {
        optionFontSizeMedium: `${15 / 192}rem`,
        optionFontSizeLarge: `${15 / 192}rem`,
        optionHeightMedium: `${34 / 192}rem`,
      },
    },
  },
  Form: {
    labelFontSizeLeftMedium: `${14 / 192}rem`,
    labelFontSizeTopMedium: `${14 / 192}rem`,
    feedbackFontSizeMedium: `${12 / 192}rem`,
    labelTextColor: '#666',
  },
  Modal: {
    peers: {
      Card: {},
    },
  },
  Dialog: {
    titleFontSize: `${18 / 192}rem`,
    titleFontWeight: 'bold',
    contentMargin: '14px 0 36px 0',
    textColor: '#666',
    peers: {
      Button: {
        borderRadiusSmall: '0',
        colorWarning: primaryColor,
        borderWarning: primaryColor,
        colorHoverWarning: '#3300f0',
        borderHoverWarning: primaryColor,
        colorPressedWarning: colorPressed,
      },
    },
  },
  Switch: {
    railColorActive: primaryColor,
  },
  Menu: {
    itemColorActive: primaryColor,
    itemTextColorActive: '#fff',
    itemColorHover: primaryColor,
    itemTextColorHover: '#fff',
    itemColorActiveHover: primaryColor,
    itemTextColorActiveHover: '#fff',
    itemTextColor: '#262626',
    itemIconColorActiveHover: '#fff',
    itemIconColorHover: '#fff',
    itemIconColorActive: '#fff',

    itemTextColorChildActive: primaryColor,
    itemTextColorChildActiveHover: '#fff',
    arrowColorChildActiveHover: '#fff',
    itemIconColorChildActiveHover: '#fff',
    itemHeight: `${42 / 191}rem`,
    arrowColorHover: '#fff',
  },
  Checkbox: {
    fontSizeMedium: `${14 / 192}rem`,
  },
  Radio: {
    fontSizeMedium: `${14 / 192}rem`,
  },
  Steps: {
    indicatorBorderColorFinish: primaryColor,
    indicatorTextColorFinish: primaryColor,
    splitorColorFinish: primaryColor,
    headerTextColorFinish: primaryColor,
    descriptionTextColorFinish: primaryColor,
    indicatorBorderColorProcess: '#262626',
    indicatorColorProcess: '#fff',
    indicatorTextColorProcess: '#262626',
    stepHeaderFontSizeSmall: `${16 / 191}rem`,
    stepHeaderFontWeight: 'bold',
    // indicatorSizeSmall: `${24 / 191}rem`,
  },
};
</script>

<style lang="scss">
#app {
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #2c3e50;
  min-width: 1000px;
}
</style>
